import type { ReactNode } from "react";
import "./legal-auth.css";

export type LegalPageKind = "terms" | "privacy" | "minors" | "operator" | "deletion";

export const LEGAL_PAGE_PATHS: Record<LegalPageKind, string> = {
  terms: "/legal/terms",
  privacy: "/legal/privacy",
  minors: "/legal/minors",
  operator: "/legal/operator",
  deletion: "/legal/deletion",
};

export const LEGAL_PAGE_VERSIONS: Record<LegalPageKind, string> = {
  terms: "1.0.0",
  privacy: "1.0.0",
  minors: "1.0.0",
  operator: "1.0.0",
  deletion: "1.0.0",
};

export interface LegalPageProps {
  kind: LegalPageKind;
  onBack?: () => void;
  operatorName?: string;
  contactEmail?: string;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return <section><h2>{title}</h2>{children}</section>;
}

function Terms() {
  return <>
    <Section title="1. この規約について"><p>本規約は、TOINOWA（以下「本サービス」）の利用条件を定めます。登録画面で内容を個別に確認し、同意した方だけが利用できます。</p></Section>
    <Section title="2. サービスの特徴"><p>本サービスは、問いに先に回答してから匿名集計や他の回答者の視点を開示する「ブラインド原則」を採用します。集計結果やパラメータは診断、評価、医療・法律・投資等の専門的助言ではありません。</p></Section>
    <Section title="3. アカウント"><ul><li>利用者は13歳以上である必要があります。</li><li>登録情報は正確に保ち、認証情報を第三者と共有しないでください。</li><li>不正利用のおそれがある場合は速やかにパスワードを変更してください。</li></ul></Section>
    <Section title="4. 投稿と回答"><p>利用者は投稿する内容について必要な権利を有するものとします。本サービスの提供、表示、モデレーションに必要な範囲で、運営者が当該内容を取り扱うことを許諾します。著作権そのものが運営者へ移転するものではありません。</p></Section>
    <Section title="5. 禁止事項"><ul><li>他者への脅迫、差別、嫌がらせ、個人情報の暴露</li><li>違法行為、自傷・自殺の助長、性的搾取、未成年者を危険にさらす行為</li><li>なりすまし、スパム、不正アクセス、集計や投票の操作</li><li>本サービスまたは第三者の権利を侵害する行為</li></ul></Section>
    <Section title="6. 対応措置"><p>違反または安全上の懸念がある場合、運営者は投稿の非表示、機能制限、アカウント停止、関係機関への連絡等を行うことがあります。利用者はアプリ内から問題を報告できます。</p></Section>
    <Section title="7. 変更・停止と責任"><p>保守、安全確保、法令対応等のため、事前の案内を行ったうえで本サービスを変更・停止することがあります。運営者は故意または重過失がある場合を除き、通常かつ直接の損害の範囲で責任を負います。消費者契約法その他の強行法規は優先して適用されます。</p></Section>
    <Section title="8. 規約の変更"><p>重要な変更は施行前に本サービス上で通知し、必要な場合は改めて同意を取得します。適用法は日本法とし、紛争は法令上認められる管轄裁判所で解決します。</p></Section>
  </>;
}

function Privacy() {
  return <>
    <Section title="1. 取得する情報"><ul><li>メールアドレス、表示名、認証・セッション情報</li><li>問い、選択した回答、理由、考え直し、保存した視点</li><li>任意で登録する年代、性別、広域地域、立場・職業、関心タグ</li><li>不正対策に必要なIPアドレス、端末・ブラウザ情報、操作記録</li><li>通報、ブロック、問い合わせに関する情報</li></ul></Section>
    <Section title="2. 利用目的"><p>本サービスの提供、本人認証、問いの配信、匿名集計、固定ルールによる傾向表示、安全対策、問い合わせ対応、品質改善、法令遵守に利用します。現行版の傾向表示はAIによる人物評価ではありません。</p></Section>
    <Section title="3. 属性情報と集計"><p>属性の登録は任意で、「回答しない」を選べます。個人の属性と回答の組み合わせを質問者へ開示しません。属性別集計は、区分内の各選択肢セルがそれぞれ20回答以上になった場合に限って表示します。</p></Section>
    <Section title="4. 外部サービス"><p>インフラ、不正アクセス対策、メール配信等のため、Cloudflareおよび設定されたメール配信事業者に必要最小限の情報を送信する場合があります。委託先を利用する場合は契約と設定により適切な管理を行います。</p></Section>
    <Section title="5. 保存期間と削除"><p>アカウント存続中はサービス提供に必要な期間保存します。退会時はプロフィールと認証情報を削除し、既存の回答は集計の整合性を保つため利用者との結び付きを外して匿名化する場合があります。法令、安全対応、紛争対応に必要な情報は必要期間に限り保管します。</p></Section>
    <Section title="6. 利用者の選択"><p>プロフィール公開、属性を使った配信、任意属性は設定から変更できます。アカウント削除、保有情報に関する照会、訂正等は「アカウント・データ削除窓口」から申請できます。</p></Section>
    <Section title="7. セキュリティと改定"><p>アクセス制御、暗号化通信、レート制限、監査記録等の合理的な安全管理措置を講じます。重要な変更は事前に通知し、必要な場合は新しい版への同意を取得します。</p></Section>
  </>;
}

function Minors() {
  return <>
    <Section title="利用できる年齢"><p><strong>13歳未満の方は本サービスへ登録できません。</strong> 登録時に「13歳以上であること」を独立した項目で確認します。</p></Section>
    <Section title="13歳から17歳の方へ"><p>保護者と利用規約・プライバシーポリシーを一緒に確認し、保護者の同意を得てから利用してください。学校名、住所、電話番号、現在地など、本人を特定できる情報は投稿しないでください。</p></Section>
    <Section title="安全のために"><ul><li>不快な問いや回答には反応せず、通報・ブロックを利用してください。</li><li>危険が迫っているときは、アプリだけで解決しようとせず、保護者、学校、警察や公的相談窓口へ相談してください。</li><li>年齢要件を満たさないアカウントを確認した場合は利用停止と削除を行います。</li></ul></Section>
    <Section title="保護者の方へ"><p>お子さまのアカウントやデータの削除相談は「アカウント・データ削除窓口」からご連絡ください。本人確認のため、必要最小限の情報をお願いする場合があります。</p></Section>
  </>;
}

function Operator({ operatorName, contactEmail }: Required<Pick<LegalPageProps, "operatorName" | "contactEmail">>) {
  return <>
    <Section title="サービス情報"><dl><div><dt>サービス名</dt><dd>TOINOWA</dd></div><div><dt>運営者</dt><dd>{operatorName}</dd></div><div><dt>連絡先</dt><dd><a href={`mailto:${contactEmail}`}>{contactEmail}</a></dd></div></dl></Section>
    <Section title="お問い合わせ"><p>不具合、権利侵害、個人情報、未成年者の利用、削除に関するご連絡を受け付けます。安全上緊急の危険がある場合、本窓口ではなく警察・消防等の緊急窓口を利用してください。</p></Section>
    <Section title="テスト環境について"><p>現在は招待制のテスト環境です。検索エンジンへの掲載を抑止し、機能・安全性・運用手順を検証しています。一般公開前に、運営主体に関する表示と問い合わせ体制を再確認します。</p></Section>
  </>;
}

function Deletion({ contactEmail }: Required<Pick<LegalPageProps, "contactEmail">>) {
  const subject = encodeURIComponent("TOINOWA アカウント・データ削除の相談");
  return <>
    <Section title="アプリ内で退会する"><ol><li>「わたし」タブを開きます。</li><li>設定の「退会する」を選びます。</li><li>表示された削除内容を確認し、確認文言を入力して確定します。</li></ol><p>退会すると認証情報、プロフィール、関心タグ、保存情報、同意履歴を削除します。回答は集計の整合性を保つため、アカウントとの結び付きを外して匿名化する場合があります。</p></Section>
    <Section title="ログインできない・保護者からの依頼"><p>登録メールアドレス、表示名、相談内容を記載し、<a href={`mailto:${contactEmail}?subject=${subject}`}>{contactEmail}</a> へご連絡ください。本人確認後に対応します。パスワードや本人確認に不要な公的証明書番号は送らないでください。</p></Section>
    <Section title="対応と例外"><p>本人確認や不正な削除の防止に必要な期間を要する場合があります。法令上の保存義務、権利保全、安全対応に必要な情報は、目的に必要な範囲と期間に限って保管し、その後削除します。</p></Section>
  </>;
}

const TITLES: Record<LegalPageKind, string> = {
  terms: "利用規約",
  privacy: "プライバシーポリシー",
  minors: "未成年の利用方針",
  operator: "運営者情報",
  deletion: "アカウント・データ削除窓口",
};

export function LegalPage({
  kind,
  onBack,
  operatorName = "TOINOWA運営事務局",
  contactEmail = "support@suigetsu.cc",
}: LegalPageProps) {
  return <main className="legal-shell">
    <header className="legal-header">
      {onBack ? <button type="button" onClick={onBack}>← 戻る</button> : <a href="/">← アプリへ戻る</a>}
      <span>TOINOWA</span>
    </header>
    <article className="legal-document">
      <p className="legal-kicker">LEGAL &amp; SAFETY</p>
      <h1>{TITLES[kind]}</h1>
      <p className="legal-meta">第{LEGAL_PAGE_VERSIONS[kind]}版　制定・施行 2026年8月11日</p>
      {kind === "terms" && <Terms />}
      {kind === "privacy" && <Privacy />}
      {kind === "minors" && <Minors />}
      {kind === "operator" && <Operator operatorName={operatorName} contactEmail={contactEmail} />}
      {kind === "deletion" && <Deletion contactEmail={contactEmail} />}
    </article>
    <LegalFooter />
  </main>;
}

export function LegalFooter() {
  return <footer className="legal-footer" aria-label="法務・安全情報">
    {(Object.keys(LEGAL_PAGE_PATHS) as LegalPageKind[]).map((kind) => <a href={LEGAL_PAGE_PATHS[kind]} key={kind}>{TITLES[kind]}</a>)}
  </footer>;
}
